// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import {
  fetchDashboardsPoolResourceExportJobDetail,
  fetchDashboardsPoolResources,
  startExportDashboardsPoolResources,
} from 'apis/dashboards/dashboardApi';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { DEFAULT_PAGE } from 'util/ConstantValues';
import { DashboardsPoolResources } from 'util/Types';
import { PoolDashboardMasterFilter } from '../PoolDashboardFilter';
import PoolDashboardResourceTable from './PoolDashboardResourceTable';
import styles from './PoolDashboardResourceTable.module.scss';
import { useCallbackLoading } from 'util/hooks/useCallbackLoading';
import LoadingCoating from 'components/atoms/LoadingCoating';
import { LoadingSpiner } from 'components/atoms/LoadingSpiner';
import { useCheckPoolDashboardExporting } from 'util/hooks/useCheckPoolDashboardExporting';
import Popover from 'components/atoms/Popover';

type PoolDashboardResourceDataProps = {
  filter?: PoolDashboardMasterFilter;
  fetchTrigger?: number;
};

const PoolDashboardResourceData: React.FC<PoolDashboardResourceDataProps> = ({
  filter,
  fetchTrigger,
}) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [dashboardPoolResources, setDashboardPoolResources] = useState<
    DashboardsPoolResources[]
  >([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [total, setTotal] = useState<number>(0);
  const { exportedJobData, isProcessing, failedMessage, setIsProcessing } =
    useCheckPoolDashboardExporting();

  const { triggerFetch, loading } = useCallbackLoading({
    callback: (p: { page: number; filter?: PoolDashboardMasterFilter }) =>
      fetchDashboardsPoolResources({
        params: {
          ...p.filter,
          page: p.page,
        },
        successCallback: ({ data, total }) => {
          setDashboardPoolResources(data);
          setPage(p.page);
          setTotal(total);
        },
        hideLoading: true,
      }),
  });

  useEffect(() => {
    triggerFetch({
      page: DEFAULT_PAGE,
      filter,
    });
  }, [filter, fetchTrigger]);

  const handleDownloadCSV = async () => {
    if (isExporting || isProcessing) {
      return;
    }

    setIsExporting(true);

    await startExportDashboardsPoolResources({
      params: filter,
      successCallback: ({ jobId }) =>
        setIsProcessing({
          jobId,
        }),
      failedCallback: () => setIsExporting(false),
      finallyCallback: () => setIsExporting(false),
    });
  };

  const handleDownloadFile = async (jobId: number) => {
    setIsDownloading(true);

    await fetchDashboardsPoolResourceExportJobDetail({
      jobId,
      successCallback: (data) => {
        if (data?.downloadUrl) {
          window.open(data.downloadUrl, '_blank');
        }
      },
      finallyCallback: () => setIsDownloading(false),
    });
  };

  const renderExportingText = () => {
    if (isExporting) {
      return (
        <p>{t('pool_dashboard.resources_table.export.starting_export')}</p>
      );
    }

    if (isProcessing) {
      return (
        <p>{t('pool_dashboard.resources_table.export.export_processing')}</p>
      );
    }

    if (failedMessage) {
      return (
        <p
          style={{
            color: '#ff3424',
          }}
        >
          {failedMessage}
        </p>
      );
    }

    if (exportedJobData?.downloadUrl) {
      const filename =
        exportedJobData.downloadUrl.match(/\/([^\\/?#]+)(?:[?#]|$)/)?.[1] ||
        `${user.name}.csv`;

      return (
        <p
          style={{
            color: '#86EA5C',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
          onClick={() => handleDownloadFile(exportedJobData.id)}
        >
          {!isDownloading ? (
            <IconDownload strokeColor="#86EA5C" />
          ) : (
            <LoadingSpiner width={16} height={16} color="#86EA5C" />
          )}
          {filename}
        </p>
      );
    }

    return null;
  };

  return (
    <>
      <div className={styles.PoolDashboardResourceData}>
        <LoadingCoating
          show={loading}
          styles={{
            borderRadius: 12,
          }}
        />

        <div className={styles.Header}>
          <p>{t('pool_dashboard.resources_table.title')}</p>

          <div className={styles.ExportSection}>
            {renderExportingText()}

            <Popover
              content={
                isExporting || isProcessing
                  ? null
                  : t('pool_dashboard.resources_table.export.start_new_export')
              }
              positions={['top']}
            >
              <div
                className={styles.DownloadButton}
                onClick={handleDownloadCSV}
              >
                {isExporting || isProcessing ? (
                  <LoadingSpiner width={24} height={24} color="#B7B7B7" />
                ) : (
                  <IconDownload />
                )}
              </div>
            </Popover>
          </div>
        </div>

        <PoolDashboardResourceTable
          dashboardPoolResources={dashboardPoolResources}
          total={total}
          page={page}
          onPageChange={(page) => triggerFetch({ page, filter })}
        />
      </div>
    </>
  );
};

export default PoolDashboardResourceData;
